import { agentTeamRequestApi } from '$/api/agentTeamRequest';
import APP from '$/api/api';
import { ProjectType, type SubmitRoomParams } from '$/api/server.types';
import type { RecordCategoryType } from '$/domains/record/type';
import type {
  AgentTeamFileTreeData,
  FlowToolEventInput,
  FlowToolEventParams,
  LegacyFlowToolEventParams,
  AgentTeamMembershipInfoParams,
  AgentTeamMembershipInfoResult,
  AgentTeamMembershipInfoResponse,
  AgentTeamCoinProductsParams,
  AgentTeamCoinProductsResult,
  AgentTeamDownloadUrlResolveParams,
  AgentTeamDownloadUrlResolveResponse,
  AgentTeamDownloadUrlResolveResult,
  AgentTeamSubscriptionProductsParams,
  AgentTeamSubscriptionProductsResult,
  GetAgentTeamDocTreeParams,
  GetAgentTeamRecordListParams,
  GetAgentTeamRecordListResult,
  TeamAgentDocTreeNode,
  ToolExecuteParams,
  ToolExecuteResult,
  ToolQueryResult,
  ToolsCountCostParam,
} from './type';

/**
 * 解析 Team 商业化会员信息接口的新响应，失败时保留原始响应便于排查。
 */
function parseAgentTeamMembershipInfoResult(responseData?: AgentTeamMembershipInfoResponse): AgentTeamMembershipInfoResult {
  if (responseData?.code === 0 && responseData.result) {
    return responseData.result;
  }

  const error = new Error('getAgentTeamMembershipInfo 失败');
  Object.assign(error, { responseData });
  throw error;
}

/**
 * 解析媒体下载 URL 接口响应，兼容 YApi 文档的 error_code 和真实接口的 code 返回结构。
 */
function parseAgentTeamDownloadUrlResolveResult(responseData?: AgentTeamDownloadUrlResolveResponse): AgentTeamDownloadUrlResolveResult {
  const successCode = responseData?.code ?? responseData?.error_code;
  if (successCode === 0 && responseData?.result?.download_url) {
    return responseData.result;
  }

  const error = new Error(responseData?.message || responseData?.error_msg || 'resolveAgentTeamDownloadUrl 失败');
  Object.assign(error, { responseData });
  throw error;
}

export interface ArtifactReportParams {
  room_id: string;
  task_id: string;
  block_id: string;
  /** 上报类型：0 默认上画布，1 用户下载保存；不传时请求体默认补 0。 */
  report_type?: number;
}

function asNodeArray(v: unknown): unknown[] | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v : undefined;
}

function strField(v: unknown): string | undefined {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return undefined;
}

/** YApi #222062 子树节点归一化（兼容新格式：type=directory/file, result 为数组） */
function normalizeFileTreeNode(raw: unknown): TeamAgentDocTreeNode | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const children = asNodeArray(o.children);
  const nodeTypeRaw = strField(o.type);
  const nodeType = nodeTypeRaw === 'directory' ? 'folder' : nodeTypeRaw;
  const path = strField(o.path);

  const node: TeamAgentDocTreeNode = {
    id: strField(o.id) ?? path ?? strField(o.name),
    biz_id: strField(o.biz_id),
    parent_id: strField(o.parent_id),
    name: strField(o.name),
    type: nodeType,
    url: strField(o.url),
    water_mark_url: strField(o.water_mark_url),
    created_at: strField(o.created_at),
  };

  if (o.media != null && typeof o.media === 'object') {
    const m = o.media as Record<string, unknown>;
    node.media = {
      width: Number(m.width) || 0,
      height: Number(m.height) || 0,
      size: Number(m.size) || 0,
      duration: Number(m.duration) || 0,
    };
  }

  if (children?.length) {
    node.children = children
      .map((item) => normalizeFileTreeNode(item))
      .filter((n): n is TeamAgentDocTreeNode => n != null);
  }

  const hasPayload = node.id || node.name || node.biz_id || node.children?.length;
  return hasPayload ? node : null;
}

/**
 * 将原始数组节点归一化为可渲染树列表。
 */
function normalizeNodeList(rawList: unknown[]): TeamAgentDocTreeNode[] {
  return rawList
    .map((item) => normalizeFileTreeNode(item))
    .filter((n): n is TeamAgentDocTreeNode => n != null);
}

function parseFileTreePayload(raw: unknown): AgentTeamFileTreeData | null {
  // 新格式：result 是完整树数组
  const resultList = asNodeArray(raw);
  if (!resultList?.length) return null;
  const roots = normalizeNodeList(resultList);
  return roots.length ? roots : null;
}

/**
 * Agent Team 房间在 canvas create / duplicate 绑定成功后写入历史列表；失败不阻断主流程。
 *
 * @param {string} roomId
 */
export async function submitAgentTeamRoomOnCreate(roomId: string) {
  const normalizedRoomId = String(roomId || '').trim();
  if (!normalizedRoomId) {
    return;
  }

  try {
    await submitAgentTeamRoom({
      room_id: normalizedRoomId,
      room_type: ProjectType.AgentTeam,
    });
  } catch (error) {
    console.error('[submitAgentTeamRoomOnCreate] failed', { roomId: normalizedRoomId, error });
  }
}

/**
 * 创建 Agent 团队房间
 */
export async function createAgentTeamRoom() {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.createAgentTeamRoom,
    method: 'POST',
    data: {
      room_type: ProjectType.AgentTeam,
    },
  });
  return response;
}

/**
 * 保存房间到房间列表
 */
export async function submitAgentTeamRoom(params: { room_id: string; room_type: ProjectType; }) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.submitAgentTeamRoom,
    method: 'POST',
    data: params,
  });
  if (response?.data?.code === 0) {
    return response?.data;
  }
  throw new Error('submitAgentTeamRoom 失败');
}

/**
 * 取消 Agent 团队消息流
 */
export async function createAgentTeamStream(params: any) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.cancelAgentTeamStream,
    method: 'POST',
    data: params,
  });
  return response;
}

/**
 * 提交询问卡片回复。
 */
export async function replyAgentTeamStream(params: any) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.replyAgentTeamStream,
    method: 'POST',
    data: params,
  });
  if (response?.data?.code === 0) {
    return response?.data || response;
  }
  throw response?.data || response || new Error('replyAgentTeamStream 失败');
}

/** 团队房间历史详情；author_uid 可选，分享等场景由调用方传入时带给后端。 */
export interface GetAgentTeamMessageHistoryDetailParams {
  room_id: string;
  author_uid?: string;
}

export async function getAgentTeamMessageHistoryDetail(
  params: GetAgentTeamMessageHistoryDetailParams,
) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.agentTeamMessageHistoryDetail,
    method: 'POST',
    data: params,
  });
  return response;
}

/** Agent Team 分享详情请求参数；公共参数与鉴权头由 agentTeamRequestApi 统一补齐。 */
export interface GetAgentTeamShareDetailParams {
  short_link: string;
  create_from?: string;
}

/** 通过分享短链获取只读消息详情。 */
export async function getAgentTeamShareDetail(
  params: GetAgentTeamShareDetailParams,
) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.agentTeamShareDetail,
    method: 'POST',
    data: params,
  });
  return response;
}

/**
 * 修改 Agent 团队房间标题
 * 与 RoboneoAgent 的历史标题修改逻辑保持一致，只是切到 Team API。
 */
export async function editAgentTeamRoomInfo(params: {
  room_id: string;
  message: string;
}) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.agentTeamModifyHistory,
    method: 'POST',
    data: params,
  });
  if (response?.data?.code === 0) {
    return response?.data;
  }
  throw response?.data || new Error('editAgentTeamRoomInfo 失败');
}

/**
 * 文件系统-获取房间文件列表（YApi #222062）
 * 与 getAgentTeamRecordList 一致：只取 response.data.result，再归一化为树根数组
 */
export async function getAgentTeamDocTree(
  params: GetAgentTeamDocTreeParams,
): Promise<AgentTeamFileTreeData | null> {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.agentTeamFileSystemTree,
    method: 'POST',
    data: params,
  });
  const data = response?.data?.result;
  return parseFileTreePayload(data);
}

/**
 * Agent Teams 列表，room_type 由调用方传入，返回格式与 getRecordList 一致
 */
export async function getAgentTeamRecordList(
  params: GetAgentTeamRecordListParams,
): Promise<GetAgentTeamRecordListResult> {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.agentTeamRecordList,
    method: 'POST',
    data: params,
  });
  const data = response?.data?.result ?? {};
  const list = (data.list ?? []) as RecordCategoryType[];
  return {
    list,
    bottomDesc: data.bottom_desc ?? '',
    totalCount: data.total_count ?? 0,
  };
}

/**
 * 生成分享短链
 */
export async function createAgentTeamShareLink(roomId: string) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.createAgentTeamShareLink,
    method: 'POST',
    data: {
      room_id: roomId,
    },
  });
  return response?.data?.result?.short_link;
}

/**
 * 通过分享短链获取 room_id
 */
export async function resolveAgentTeamShareLink(
  short_link: string,
  create_from?: string,
): Promise<{ room_id: string; author_uid: string; }> {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.resolveAgentTeamShareLink,
    method: 'POST',
    data: {
      short_link,
      create_from,
    },
  });
  return response?.data?.result ?? { room_id: '', author_uid: '' };
}

/**
 * 画布编辑推送事件：支持 `operations` 批量；旧平铺参数会归一成单条 operation 再请求
 *
 * @export
 * @param {string} roomId
 * @param {FlowToolEventInput} params
 * @return {*}
 */
export async function flowToolEvent(
  roomId: string,
  params: FlowToolEventInput,
) {
  let body: Record<string, unknown>;
  if ('operations' in params && params.operations != null) {
    const { operations, main_agent_id, sub_agent_id } = params as FlowToolEventParams;
    body = {
      room_id: roomId,
      operations,
      main_agent_id,
      sub_agent_id,
    };
  } else {
    const {
      call_tools,
      origin_url,
      diff,
      new_url,
      resource_type,
      action,
      name,
      amt,
      main_agent_id,
      sub_agent_id,
    } = params as LegacyFlowToolEventParams;
    body = {
      room_id: roomId,
      main_agent_id,
      sub_agent_id,
      operations: [
        {
          resource_type,
          action,
          origin_url,
          new_url,
          ...(name !== undefined ? { name } : {}),
          ...(amt !== undefined ? { amt } : {}),
          ...(diff !== undefined ? { diff } : {}),
          ...(call_tools ? { call_tools: [call_tools] } : {}),
        },
      ],
    };
  }
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.agentTeamFlowToolEvent,
    method: 'POST',
    data: body,
  });
  if (
    response?.data?.code === 0
    && response?.data?.result.status === 'success'
  ) {
    return true;
  }
  throw new Error('flowToolEvent 失败');
}

/**
 * 工具执行
 *
 * @export
 * @param {string} roomId
 * @param {ToolExecuteParams} parmas
 * @return {*}
 */
export async function toolExecute(
  roomId: string,
  parmas: ToolExecuteParams,
): Promise<ToolExecuteResult> {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.toolExecute,
    method: 'POST',
    data: {
      room_id: roomId,
      node_list_array: [[parmas]],
    },
  });
  return response?.data;
}

/**
 * 文本差异对比
 *
 * @export
 * @param {string} roomId
 * @param {string} taskId
 * @return {*}  {Promise<ToolQueryResult>}
 */
export async function toolQuery(
  roomId: string,
  taskId: string,
): Promise<ToolQueryResult> {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.toolQuery,
    method: 'POST',
    data: {
      room_id: roomId,
      task_ids: [taskId],
    },
  });
  return response?.data;
}

/**
 * 文本差异对比
 *
 * @export
 * @param {string} oldMarkdown
 * @param {string} newMarkdown
 * @return {*}
 */
export async function markdownDiff(origin_url: string, new_url: string) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.markdownDiff,
    method: 'POST',
    data: {
      origin_url,
      new_url,
    },
  });
  if (response?.data?.code === 0) {
    return response?.data?.result;
  }
  throw new Error('markdownDiff 失败');
}

/**
 * 计费查询
 *
 * @export
 * @param {ToolsCountCostParam} param
 * @return {*}
 */
export async function getCountCost(param: ToolsCountCostParam) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.countcost,
    method: 'POST',
    data: {
      items: [param],
    },
  });
  if (response?.data?.code === 0) {
    return response?.data.result;
  }
  throw new Error('getCountCost 失败');
}

/**
 * 检查房间是否存在
 *
 * @export
 * @param {string} room_id
 * @return {*}
 */
export async function roomCheck(room_id: string) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.roomCheck,
    method: 'POST',
    data: {
      room_id,
      need_half_block: true,
    },
  });
  if (response?.data?.code === 0) {
    return response?.data;
  }
  throw new Error('roomCheck 失败');
}

/**
 * 提交房间
 *
 * @export
 * @param {SubmitRoomParams} data
 * @return {*}
 */
export async function roomSubmit(data: SubmitRoomParams) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.roomSubmit,
    method: 'POST',
    data,
  });
  if (response?.data?.code === 0) {
    return response?.data;
  }
  throw new Error('roomSubmit 失败');
}

/**
 * 获取图像理解描述，用于传给agent team
 *
 * @export
 * @param {string} image_url
 * @return {*}
 */
export async function getMediaAnalysis(params: { room_id: string; media_list: { media_url: string; index: string; media_type: 'image' | 'video' | 'file'; }[]; }) {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.mediaAnalysis,
    method: 'POST',
    data: {
      ...params,
    },
  });
  if (response?.data?.code === 0) {
    return response?.data;
  }
  throw new Error('imageAnalysis 失败');
}

/**
 * 上报给服务端的哪些产物已经上过画布，没有上过的，下次进入房间时，重新上画布（续传未上画布问题）
 *
 * @export
 * @param {ArtifactReportParams | ArtifactReportParams[]} params
 * @return {*}
 */
export async function artifactReport(params: ArtifactReportParams | ArtifactReportParams[]) {
  const reportParamsList = Array.isArray(params) ? params : [params];
  const firstParams = reportParamsList[0];
  if (!firstParams) {
    throw new Error('artifactReport 参数不能为空');
  }
  const {
    room_id,
    task_id,
    block_id,
    report_type,
  } = firstParams;
  const data = {
    room_id,
    task_id,
    block_id,
    report_type,
    // 新版接口通过 reported_items 接收批量产物，单条上报也补齐该字段以兼容接口要求。
    reported_items: reportParamsList,
  };
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.artifactReport,
    method: 'POST',
    data,
  });
  if (response?.data?.code === 0) {
    return response?.data;
  }
  throw new Error('artifactReport 失败');
}

/**
 * 文本安审
 *
 * @export
 * @param {string} text
 * @return {*}  {Promise<boolean>}
 */
export async function textSafeCheck(text: string): Promise<boolean> {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.textSafeCheck,
    method: 'POST',
    data: {
      text,
    },
  });
  if (response?.data?.code === 0) {
    return response?.data.result.sensitive_result;
  }
  throw new Error('artifactReport 失败');
}

interface AgentTeamSubscriptionProductsRequestOptions {
  useTeamHeaders?: boolean;
}

interface AgentTeamSubscriptionProductsRequestConfig {
  url: string;
  method: 'POST';
  skipClientOId: boolean;
  data: AgentTeamSubscriptionProductsParams;
}

/**
 * 获取 Agent Team 域名下的订阅商品列表，团队版由公共请求层携带当前实际组织请求头。
 */
export async function getAgentTeamSubscriptionProducts(
  params: AgentTeamSubscriptionProductsParams,
  options: AgentTeamSubscriptionProductsRequestOptions = {},
): Promise<AgentTeamSubscriptionProductsResult> {
  const requestConfig: AgentTeamSubscriptionProductsRequestConfig = {
    url: APP.roboneo.api.agentTeamSubscriptionProducts,
    method: 'POST',
    skipClientOId: !options.useTeamHeaders,
    data: params,
  };
  const response = await agentTeamRequestApi?.request(requestConfig);
  if (response?.data?.code === 0) {
    return response.data.result;
  }
  throw response?.data || new Error('getAgentTeamSubscriptionProducts 失败');
}

/**
 * 获取 Agent Team 域名下的单购代币商品列表，公共参数和鉴权头由 agentTeamRequestApi 统一补齐。
 */
export async function getAgentTeamCoinProducts(
  params: AgentTeamCoinProductsParams = {},
): Promise<AgentTeamCoinProductsResult> {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.agentTeamCoinProducts,
    method: 'POST',
    data: params,
  });
  if (response?.data?.code === 0) {
    return response.data.result;
  }
  throw response?.data || new Error('getAgentTeamCoinProducts 失败');
}

/**
 * 获取 Agent Team 域名下的会员信息，用于替代旧 vipshow 并回填全局订阅 store。
 */
export async function getAgentTeamMembershipInfo(
  params: AgentTeamMembershipInfoParams = {},
): Promise<AgentTeamMembershipInfoResult> {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.agentTeamMembershipInfo,
    method: 'POST',
    data: params,
  });
  return parseAgentTeamMembershipInfoResult(response?.data);
}

/**
 * 下载前向 Agent Team 解析最终媒体 URL，由后端统一判断会员与 AIGC 水印策略。
 */
export async function resolveAgentTeamDownloadUrl(
  params: AgentTeamDownloadUrlResolveParams,
): Promise<AgentTeamDownloadUrlResolveResult> {
  const response = await agentTeamRequestApi?.request({
    url: APP.roboneo.api.agentTeamDownloadUrlResolve,
    method: 'POST',
    data: params,
  });
  return parseAgentTeamDownloadUrlResolveResult(response?.data);
}
